// [#13] Language preference persists through the i18n context; this component only owns its menu state.
import { useEffect, useId, useRef, useState } from "react";
import { useI18n, LANGUAGES } from "../lib/i18n";

export function LanguageSwitcher({ className = "" }: { className?: string }) {
  const { lang, setLang, t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!isOpen) return;

    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!switcherRef.current?.contains(event.target as Node)) setIsOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutsidePress);
    return () => document.removeEventListener("pointerdown", closeOnOutsidePress);
  }, [isOpen]);

  const closeMenu = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div ref={switcherRef} className={`relative inline-flex ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        onKeyDown={(event) => { if (event.key === "Escape") closeMenu(); }}
        aria-label={t("lang.label")}
        aria-expanded={isOpen}
        aria-controls={menuId}
        aria-haspopup="menu"
        className={`btn-ghost h-9 w-9 p-0 border border-line ${isOpen ? "bg-navy-50 border-navy-500" : ""}`}
      >
        <span className="text-xs font-bold" aria-hidden="true">{lang.toUpperCase()}</span>
      </button>
      {isOpen && (
        <div
          id={menuId}
          role="menu"
          aria-label={t("lang.label")}
          onKeyDown={(event) => { if (event.key === "Escape") closeMenu(); }}
          className="absolute right-0 top-full z-50 mt-2 w-48 rounded-xl border border-line bg-white p-1.5 shadow-lift"
        >
          <p className="px-2.5 py-2 text-xs font-semibold text-mut">{t("lang.label")}</p>
          {LANGUAGES.map((language) => {
            const isSelected = lang === language.code;
            return (
              <button
                key={language.code}
                type="button"
                role="menuitemradio"
                aria-checked={isSelected}
                onClick={() => { setLang(language.code); setIsOpen(false); }}
                className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-sm font-medium ${isSelected ? "bg-navy text-white" : "text-ink hover:bg-navy-50"}`}
              >
                <span className="min-w-6 text-xs font-bold" aria-hidden="true">{language.short}</span>
                <span className="flex-1">{language.label}</span>
                {isSelected && <span aria-hidden="true">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
